import {
    LOADING,
    ON_FAIL_REISSUE,
    ON_SUCCESS,
    ON_FAIL_REISSUE_NONPAYMENT,
    ON_FAIL_REISSUE_VARIATION,
    ON_RESET_REISSUE,
    ON_WARNING_CONFIRMATION,
    ON_CANCEL_WARNING_CONFIRMATION,
    ON_EMISSION_PENDING_VERIFICATION,
    ON_FARE_UNAVAILABLE
} from "../types/reissueTypes";
import {LOADING_TRANSACTION, ON_RECEIBE_CONFIRMATION} from "../types/transactionTypes";
import axios from "axios";
import getBookModifyRQ from "../transfer/reissueTransfer/getBookModifyRQ";
import getVariationPriceRS from "../transfer/reissueTransfer/getVariationPriceRS";
import config from "../config";
import getTotalPayment from "../transfer/reissueTransfer/getTotalPayment";
import { getTraceHeaders, syncTraceFromResponse } from "../util/tracing";
import { postService } from "../util/serviceClient";
import {
    buildPendingVerificationNotice,
    isCommunicationInterruptedResponse
} from "../util/emissionPendingVerification";
import { isFareUnavailableResponse } from "../util/offerPriceValidation";

const WARNING_CONFIRMATION_CODE = "WARNING_CONFIRMATION";
const PENDING_TICKET_CONFIRMATION_CODE = "2";
const ACCEPT_REMISSION_MODIFICATION_TYPE = "ACCEPT_REMISSION";
const REISSUE_CLICK_STATUS = "CLICK_REMITIR";
const REPRICE_CONFIRMATION_STATUS = "CLICK_REPRICE_CONFIRMATION";

const isAcceptedWarningConfirmationRQ = (RQ) =>
    RQ?.airBookModifyRQ?.modificationType === ACCEPT_REMISSION_MODIFICATION_TYPE;

const applyAcceptedWarningConfirmation = (RQ) => {
    RQ.airBookModifyRQ = {
        ...(RQ.airBookModifyRQ || {}),
        modificationType: ACCEPT_REMISSION_MODIFICATION_TYPE
    };

    return RQ;
};

const getServerErrorMessage = (errors) => {
    const error = errors?.error?.[0] || {};
    const code = error.code || "";
    const normalizedCode = code.replace(":", "");
    const message = error.shortText || error.value || "El servicio ha retornado error en su respuesta. Por favor comunicarse con el equipo de TI";
    const messageValue = error.value && error.value !== message ? error.value : "";
    const warningCodes = ["VALIDATION_ERROR"];
    const dangerCodes = ["ERROR", "BAD_REQUEST"];

    return {
        code,
        message,
        messageValue,
        type: warningCodes.includes(normalizedCode)
            ? "warning"
            : dangerCodes.includes(normalizedCode)
                ? "danger"
                : "danger"
    };
};

const hasResponseErrors = (data) => (data?.errors?.error || []).length > 0;

const getFirstSource = (RQ) => RQ?.pos?.source?.[0] || {};

const getFirstBookingReference = (RQ) => RQ?.airBookModifyRQ?.bookingReferenceID?.[0] || {};

const getCheckoutUserInfo = (RQ, travelItineraryRS) => {
    const source = getFirstSource(RQ);
    const bookingReference = getFirstBookingReference(RQ);

    return {
        userId: RQ?.airBookModifyRQ?.lastModified,
        userName: RQ?.airBookModifyRQ?.lastModified,
        accountId: source.terminalID || travelItineraryRS?.accountId,
        recordLocator: bookingReference.id || travelItineraryRS?.pnr,
        pseudoCityCode: source.pseudoCityCode,
        agentSine: source.agentSine,
        gds: source.erspuserID
    };
};

const buildCheckoutEventRQ = (params, RQ, recommendation, travelItineraryRS) => {
    const source = getFirstSource(RQ);
    const bookingReference = getFirstBookingReference(RQ);
    const totalFare = RQ?.airReservation?.priceInfo?.itinTotalFare?.[0]?.totalFare?.amount;
    const hasVariationOffer = !!params.variation;
    const selectedOfferAmount = params.variation?.finalItem?.amountText ||
        recommendation?.pricing?.finalItem?.amountText;

    return {
        processStatus: hasVariationOffer ? REPRICE_CONFIRMATION_STATUS : REISSUE_CLICK_STATUS,
        userInfo: getCheckoutUserInfo(RQ, travelItineraryRS),
        payload: {
            offerId: bookingReference.type || params.offerId,
            shoppingResponseRefID: bookingReference.instance || params.shoppingResponseRefID,
            formOfPayment: params.formOfPayment,
            paymentIssuer: params.paymentDetails?.issuer,
            hasPaymentDetails: params.formOfPayment === "CC",
            hasVariationOffer,
            pnr: bookingReference.id || travelItineraryRS?.pnr,
            orderId: bookingReference.idcontext || travelItineraryRS?.orderId,
            accountId: source.terminalID || travelItineraryRS?.accountId,
            pseudoCityCode: source.pseudoCityCode,
            gds: source.erspuserID,
            passengerCount: travelItineraryRS?.travelerInfo?.airTraveler?.length,
            totalFare,
            selectedOfferAmount
        }
    };
};

const registerCheckoutEvent = async (eventRQ) => {
    try {
        const res = await postService("checkoutEvent", eventRQ, {
            headers: getTraceHeaders()
        });
        syncTraceFromResponse(res);
    } catch (ex) {
        console.log("FAIL CHECKOUT EVENT", ex);
    }
};

const registerCheckoutBackEvent = async (eventRQ) => {
    try {
        const res = await postService("backEvent", eventRQ, {
            headers: getTraceHeaders()
        });
        syncTraceFromResponse(res);
    } catch (ex) {
        console.log("FAIL CHECKOUT BACK EVENT", ex);
    }
};

// Evidencia de que el usuario vio la pantalla de confirmación. Nunca lanza error para no bloquear la vista.
export const registerRemissionConfirmationViewedEvent = async (eventRQ) => {
    try {
        const res = await axios.post(config.services.reissue.confirmationViewedEvent, eventRQ, {
            headers: getTraceHeaders()
        });
        syncTraceFromResponse(res);
    } catch (ex) {
        console.log("FAIL REMISSION CONFIRMATION VIEWED EVENT", ex);
    }
};

const buildCheckoutPageEventRQ = (params, recommendation, travelItineraryRS, processStatus = "CONTINUAR_CHECKOUT") => {
    const ticketing = travelItineraryRS?.ticketing?.[0] || {};
    const pricingSystem = ticketing.pricingSystem || {};

    return {
        processStatus,
        userInfo: {
            accountId: travelItineraryRS?.accountId,
            recordLocator: travelItineraryRS?.pnr,
            pseudoCityCode: ticketing.pseudoCityCode,
            gds: pricingSystem.code
        },
        payload: {
            offerId: params.offerId,
            shoppingResponseRefID: params.shoppingResponseRefID,
            pnr: travelItineraryRS?.pnr,
            orderId: travelItineraryRS?.orderId,
            accountId: travelItineraryRS?.accountId,
            pseudoCityCode: ticketing.pseudoCityCode,
            gds: pricingSystem.code,
            selectedOfferAmount: recommendation?.pricing?.finalItem?.amountText,
            itineraryCount: recommendation?.itinerary?.length
        }
    };
};

export const registerCheckoutPageEvent = params => async (dispatch, getState) => {
    const { travelItineraryRS } = getState().bookingInformationReducer;
    const { recommendations } = getState().recommendationsReducer;
    const recommendation = recommendations.find(item =>
        item.offerId?.offerId === params.offerId &&
        item.shoppingResponseRefID?.shoppingResponseRefID === params.shoppingResponseRefID
    );

    if (!recommendation) {
        return;
    }

    await registerCheckoutEvent(buildCheckoutPageEventRQ(params, recommendation, travelItineraryRS));
};

export const resetReissue = () => ({
    type: ON_RESET_REISSUE
});

export const registerBackEvent = params => async (dispatch, getState) => {
    const { travelItineraryRS } = getState().bookingInformationReducer;
    const { recommendations } = getState().recommendationsReducer;
    const recommendation = recommendations.find(item =>
        item.offerId?.offerId === params.offerId &&
        item.shoppingResponseRefID?.shoppingResponseRefID === params.shoppingResponseRefID
    );

    if (!recommendation) {
        return;
    }

    await registerCheckoutBackEvent(buildCheckoutPageEventRQ(params, recommendation, travelItineraryRS, "CLICK_REGRESAR"));
};

const cloneObject = (object) => JSON.parse(JSON.stringify(object));

const getReissueServiceResponse = async (RQ) => {
    const res = await postService("modify", RQ, {
        headers: getTraceHeaders()
    });
    syncTraceFromResponse(res);

    return res;
};

const enrichReservationWithDownloadRequest = (data, RQ) => {
    const reservation = data?.successAndWarningsAndAirReservation?.[0];

    if (reservation?.ticketing && reservation?.ticketing.length) {
        const bookingReference = RQ?.airBookModifyRQ?.bookingReferenceID?.[0] || {};
        const source = RQ?.pos?.source?.[0] || {};
        const downloadRequest = {
            orderId: bookingReference.idcontext,
            recordLocator: bookingReference.id,
            pseudoCityCode: source.pseudoCityCode,
            gds: source.erspuserID
        };

        const enrichedTicketing = reservation.ticketing.map(ticket => {
            return {
                ...ticket,
                downloadRequest
            };
        });

        reservation.ticketing = enrichedTicketing;
        reservation.priceInfo = getTotalPayment(RQ);
    }
};

// Resultado no confirmado por LATAM en la remisión actual: Checkout muestra el aviso y bloquea nuevos intentos
// solo en esa instancia. No se persiste nada (no hay reintento automático).
const dispatchPendingVerification = (dispatch, travelItineraryRS) => {
    dispatch({
        type: ON_EMISSION_PENDING_VERIFICATION,
        notice: buildPendingVerificationNotice(travelItineraryRS)
    });
};

const handleReissueResponse = (dispatch, data, RQ, travelItineraryRS) => {
    if (!data) {
        dispatch({
            type: ON_FAIL_REISSUE,
            payload: { errorCode: 1001 }
        });
        return;
    }

    // Debe evaluarse antes del estado "2" (tickets pendientes).
    if (isCommunicationInterruptedResponse(data)) {
        dispatchPendingVerification(dispatch, travelItineraryRS);
        return;
    }

    // Tarifa no disponible (total negativo): no se muestra VariationOferPrice ni se permite remitir.
    if (isFareUnavailableResponse(data)) {
        dispatch({
            type: ON_FARE_UNAVAILABLE
        });
        return;
    }

    const transactionStatusCode = `${data?.transactionStatusCode || ""}`;

    // Estado "2" sin errores = reemisión válida con tickets pendientes. Con errors.error es un error del
    // backend (p. ej. advertencia de LATAM) y debe mostrarse en Checkout.
    if (transactionStatusCode === PENDING_TICKET_CONFIRMATION_CODE && !hasResponseErrors(data)) {
        enrichReservationWithDownloadRequest(data, RQ);

        dispatch({
            type: ON_SUCCESS,
            payload: data
        });
        return;
    }

    if (transactionStatusCode === WARNING_CONFIRMATION_CODE) {
        const serverError = data.errors
            ? getServerErrorMessage(data.errors)
            : null;
        dispatch({
            type: ON_WARNING_CONFIRMATION,
            message: data.errors?.error?.[0]?.value || serverError?.message || "Se requiere confirmación para continuar con la remisión.",
            pendingReissueRQ: cloneObject(RQ)
        });
        return;
    }

    if(data.errors){
        const serverError = getServerErrorMessage(data.errors);
        let errorCode = data?.transactionStatusCode || serverError.code || "";
        const errorProps = {
            errorMsg: serverError.message,
            errorMsgValue: serverError.messageValue,
            errorType: serverError.type
        };

        switch(errorCode) {
            case "RLNC03":
            case "RLNC04":
                dispatch({
                    type: ON_FAIL_REISSUE_NONPAYMENT,
                    payload: { errorCode },
                    ...errorProps
                });
            break;
            case "RLNC06":
                const variation = getVariationPriceRS(data);
                dispatch({
                    type: ON_FAIL_REISSUE_VARIATION,
                    payload: { errorCode },
                    variation,
                    hasVariationOffer: true,
                    hasAcceptedWarningConfirmation: isAcceptedWarningConfirmationRQ(RQ)
                });
            break;
            default:
                dispatchPendingVerification(dispatch, travelItineraryRS);
            break;
        }
    } else {
        enrichReservationWithDownloadRequest(data, RQ);

        dispatch({
            type: ON_SUCCESS,
            payload: data
        });
    }
};

const handleReissueException = (dispatch, ex, travelItineraryRS) => {
    //console.log(ex);
    if (isCommunicationInterruptedResponse(ex.response?.data)) {
        dispatchPendingVerification(dispatch, travelItineraryRS);
        return;
    }
    const serverError = ex.response?.data?.errors
        ? getServerErrorMessage(ex.response.data.errors)
        : null;
    dispatch({
        type: ON_FAIL_REISSUE,
        payload: { errorCode: ex.response?.data?.transactionStatusCode || serverError?.code || 1001 },
        errorMsg: serverError?.message,
        errorMsgValue: serverError?.messageValue,
        errorType: serverError?.type
    });
};

export const reissueTicket = params => async (dispatch, getState) => {
    const { travelItineraryRS } = getState().bookingInformationReducer;

    // La oferta invalidada no se remite aunque el botón siga visible.
    if (getState().reissueReducer.loading || getState().reissueReducer.fareUnavailable) {
        return;
    }

    dispatch({
        type: LOADING
    });
    try {
        const { recommendations , flightTypeOption, hasRUC, ruc} = getState().recommendationsReducer;
        const { hasAcceptedWarningConfirmation } = getState().reissueReducer;
        const recommendation = recommendations.find(item =>
            item.offerId?.offerId === params.offerId &&
            item.shoppingResponseRefID?.shoppingResponseRefID === params.shoppingResponseRefID
        );
        const RQ = getBookModifyRQ(
            params,travelItineraryRS,
            recommendation,
            flightTypeOption,
            hasRUC,
            ruc
        );
        if (params.variation && hasAcceptedWarningConfirmation) {
            applyAcceptedWarningConfirmation(RQ);
        }
        await registerCheckoutEvent(buildCheckoutEventRQ(params, RQ, recommendation, travelItineraryRS));
        //console.log("RQ - ReissueTicketing -->",RQ);
        const res = await getReissueServiceResponse(RQ);
        //console.log("RS - ReissueTicketing -->",res);
        handleReissueResponse(dispatch, res.data, RQ, travelItineraryRS);
    } catch (ex) {
        handleReissueException(dispatch, ex, travelItineraryRS);
    }
};

export const confirmWarningConfirmation = () => async (dispatch, getState) => {
    const { pendingReissueRQ, loading, fareUnavailable } = getState().reissueReducer;
    const { travelItineraryRS } = getState().bookingInformationReducer;

    if (!pendingReissueRQ || loading || fareUnavailable) {
        return;
    }

    dispatch({
        type: LOADING
    });

    try {
        const RQ = cloneObject(pendingReissueRQ);
        applyAcceptedWarningConfirmation(RQ);

        console.log("RQ - AcceptWarningConfirmation -->", RQ);
        const res = await getReissueServiceResponse(RQ);
        console.log("RS - AcceptWarningConfirmation -->", res);
        handleReissueResponse(dispatch, res.data, RQ, travelItineraryRS);
    } catch (ex) {
        handleReissueException(dispatch, ex, travelItineraryRS);
    }
};

export const cancelWarningConfirmation = () => ({
    type: ON_CANCEL_WARNING_CONFIRMATION
});

export const addConcurrence = (pnr) => async (dispatch, getState) => {
    //console.log("PNR add -->", pnr);
    dispatch({
        type: LOADING_TRANSACTION
    });
    try {
        const res = await axios.get(URL + "transaction/" + pnr);

        if (res.data) {
            dispatch({
                type: ON_RECEIBE_CONFIRMATION,
                payload: res.data
            });
        }
    } catch (ex) {
        console.log(ex);
    }
};

export const downloadTicket = async (requestBody) => {
    try {
        console.log("RQ - downloadTicket-->", requestBody);
        const resDownloadTicket = await postService("downloadTicket", requestBody, {
            headers: getTraceHeaders()
        });
        syncTraceFromResponse(resDownloadTicket);
        console.log("RS - downloadTicket-->", resDownloadTicket);
        return resDownloadTicket.data;
    } catch (ex) {
        console.log(ex);
        throw ex;
    }
};

export const setHasVariationOffer = (value) => ({
  type: 'ON_FAIL_REISSUE_VARIATION',
  payload: value,
});
